import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SidebarStateService } from '@zikata-frontend/ui';
import { AuthService } from '../../core/service/auth.service';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

type ParamFunction<T> = (params: Record<string, string>) => T;
interface RouteInfo {
  path: string;
  title: string | ParamFunction<string>;
  breadcrumb: string[] | ParamFunction<string[]>;
}

@Component({
  selector: 'app-default-layout',
  template: `
    <zikatagov-toasts></zikatagov-toasts>

    <!-- ══════════════════════════════════════════
         MOBILE HEADER (only in DOM on mobile)
         ══════════════════════════════════════════ -->
    <ng-container *ngIf="isMobile">
      <div class="mobile-header">
        <div class="mh-row1">
          <img
            src="https://zikata.africa/wp-content/uploads/2024/03/Artboard-5-copy-3-300x101.png"
            alt="Zikata" class="mh-logo"
          />
          <span class="flex-spacer"></span>
          <button class="mh-bell" aria-label="Notifications">
            <ng-icon name="saxNotificationOutline" size="20"></ng-icon>
          </button>
        </div>
        <div class="mh-row2">
          <zikatagov-user-info [mobileCompactMode]="true"></zikatagov-user-info>
        </div>
      </div>
    </ng-container>

    <!-- ══════════════════════════════════════════
         DESKTOP LAYOUT  (original working layout — untouched)
         ══════════════════════════════════════════ -->
    <div *ngIf="!isMobile" class="d-flex vh-100 position-relative">
      <zikatagov-sidebar
        (menuItemClick)="onMenuItemClick($event)"
        (logout)="handleLogout()"
      ></zikatagov-sidebar>

      <div
        class="flex-grow-1 d-flex flex-column bg-zikata"
        [ngClass]="{ 'content-expanded': !isCollapsed }"
      >
        <div class="d-flex align-items-center shadow-sm">
          <zikatagov-header class="w-100"></zikatagov-header>
        </div>
        <main class="flex-grow-1 overflow-auto px-3 pt-3 bg-light">
          <div class="container-fluid">
            <zikatagov-page-header></zikatagov-page-header>
            <router-outlet></router-outlet>
          </div>
        </main>
      </div>
    </div>

    <!-- ══════════════════════════════════════════
         MOBILE CONTENT AREA
         ══════════════════════════════════════════ -->
    <div class="mobile-shell" *ngIf="isMobile">
      <main class="mobile-main">
        <zikatagov-page-header></zikatagov-page-header>
        <router-outlet></router-outlet>
      </main>
    </div>

    <!-- ══════════════════════════════════════════
         MOBILE BOTTOM NAV
         ══════════════════════════════════════════ -->
    <zikatagov-mobile-bottom-nav
      *ngIf="isMobile"
      (menuItemClick)="onMenuItemClick($event)"
    ></zikatagov-mobile-bottom-nav>
  `,
  styles: [`
    :host { display: block; width: 100%; height: 100%; }

    .flex-spacer { flex: 1; }

    /* ─── Mobile header ─── */
    .mobile-header {
      position: fixed;
      top: 0; left: 0; right: 0;
      z-index: 1050;
      background: #fff;
      border-bottom: 1px solid #f0eafa;
      box-shadow: 0 2px 16px rgba(109,40,217,.08);
      /* overflow:visible so ngb dropdowns are NOT clipped */
      overflow: visible;
    }

    .mh-row1 {
      display: flex;
      align-items: center;
      height: 52px;
      padding: 0 1rem;
      gap: .75rem;
    }

    .mh-logo { height: 28px; width: auto; object-fit: contain; }

    .mh-bell {
      display: flex; align-items: center; justify-content: center;
      width: 38px; height: 38px;
      border: 1.5px solid #f0eafa; background: #faf8ff;
      border-radius: 10px; cursor: pointer; color: #7c3aed;
      flex-shrink: 0;
      -webkit-tap-highlight-color: transparent;
    }
    .mh-bell:active { background: #ede9fb; }

    .mh-row2 {
      display: flex;
      align-items: center;
      min-height: 56px;
      padding: .5rem .875rem;
      border-top: 1px solid #f0eafa;
      background: #fdfbff;
      /* overflow:visible so dropdowns escape */
      overflow: visible;
    }

    /* ─── Mobile content shell ───
       header: 52px + 56px = 108px
       bottom nav: 60px                  */
    .mobile-shell {
      position: fixed;
      top: 108px;
      left: 0; right: 0;
      bottom: 60px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .mobile-main {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      background: #f8fafc;
      padding-bottom: 8px;
      -webkit-overflow-scrolling: touch;
    }
    .mobile-main::-webkit-scrollbar { display: none; }
  `],
  styleUrls: ['./default-layout.component.scss'],
  standalone: false,
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  isMobile = false;
  isCollapsed = false;

  private sidebarSubscription!: Subscription;
  private resizeSubscription!: Subscription;

  private routeMap: Record<number, RouteInfo> = {
    0:   { path: '/dashboard/home',                                         title: 'Home',                      breadcrumb: ['Dashboard', 'Home'] },
    3:   { path: '/dashboard/applications',                                 title: 'My Applications',           breadcrumb: ['Dashboard', 'Applications'] },
    9:   { path: '/dashboard/alerts',                                       title: 'Alerts',                    breadcrumb: ['Dashboard', 'Alerts'] },
    4:   { path: '/dashboard/developer',                                    title: 'Developer',                 breadcrumb: ['Dashboard', 'Developer'] },
    5:   { path: '/dashboard/transaction',                                  title: 'Transactions',              breadcrumb: ['Dashboard', 'Transactions'] },
    6:   { path: '/dashboard/users',                                        title: 'Display Users',             breadcrumb: ['Dashboard', 'Users'] },
    7:   { path: '/dashboard/contacts',                                     title: 'Contacts',                  breadcrumb: ['Dashboard', 'Contacts'] },
    8:   { path: '/dashboard/campaign',                                     title: 'SMS Builder',               breadcrumb: ['Dashboard', 'Campaign'] },
    11:  { path: '/contact-management/campaigns',                           title: 'Bulk SMS',                  breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS'] },
    10:  { path: '/contact-management/groups',                              title: 'Contact Groups',            breadcrumb: ['Dashboard', 'Contact', 'Contact Groups'] },
    12:  { path: '/contact-management/surveys',                             title: 'Surveys',                   breadcrumb: ['Dashboard', 'Surveys'] },
    115: { path: '/dashboard/whatsapp',                                     title: 'WhatsApp Messages',         breadcrumb: ['Dashboard', 'WhatsApp'] },
    116: { path: '/dashboard/summary',                                      title: 'Summary',                   breadcrumb: ['Dashboard', 'Summary'] },
    100: { path: '/contact-management/groups/:id',
           title: (params) => `Contact Groups`,
           breadcrumb: (params) => ['Dashboard', 'Contact', 'Contact Groups', 'Updating'] },
    101: { path: '/contact-management/campaigns/new',                       title: 'Bulk SMS',                  breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS', 'New'] },
    103: { path: '/contact-management/surveys/form/:id',
           title: (params) => `Survey`,
           breadcrumb: (params) => ['Dashboard', 'Survey', 'Updating'] },
    104: { path: '/contact-management/surveys/form/new',                    title: 'Survey',                    breadcrumb: ['Dashboard', 'Survey', 'New', 'New'] },
    105: { path: '/contact-management/surveys/questions/:id',
           title: (params) => `Survey Question`,
           breadcrumb: (params) => ['Dashboard', 'Survey', 'Questions', 'Updating'] },
    106: { path: '/contact-management/surveys/outro/:id',
           title: (params) => `Survey Outro`,
           breadcrumb: (params) => ['Dashboard', 'Survey', 'Outro'] },
    107: { path: '/contact-management/surveys/send/:id',
           title: (params) => `Publish Survey`,
           breadcrumb: (params) => ['Dashboard', 'Survey', 'Send Survey', 'Updating'] },
    108: { path: '/contact-management/surveys/responses/:id',
           title: (params) => `Survey Responses`,
           breadcrumb: (params) => ['Dashboard', 'Survey', 'Responses'] },
    109: { path: '/contact-management/campaigns/form/:id',
           title: (params) => `Edit Bulk SMS Campaign`,
           breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS', 'Updating'] },
    110: { path: '/contact-management/groups/new',                          title: 'New Contact Group',          breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'New'] },
    111: { path: '/contact-management/groups/:id/edit',
           title: (params) => 'Edit Contact Group',
           breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Edit'] },
    112: { path: '/contact-management/groups/:id/import',
           title: (params) => 'Import Contacts',
           breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Import'] },
    113: { path: '/contact-management/groups/:id/contacts/new',
           title: (params) => 'New Contact',
           breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'New Contact'] },
    114: { path: '/contact-management/groups/:id/contacts/:contactId/edit',
           title: (params) => 'Edit Contact',
           breadcrumb: ['Dashboard', 'Contact', 'Contact Groups', 'Edit Contact'] },
    120: { path: '/contact-management/campaigns/dynamic',
           title: 'Dynamic Messages',
           breadcrumb: ['Dashboard', 'SMS', 'Bulk SMS', 'Dynamic Messages'] },
  };

  constructor(
    private router: Router,
    private sidebarStateService: SidebarStateService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
    // Run in constructor so isMobile is set before first template render
    this.checkScreenSize();
  }

  ngOnInit(): void {
    this.sidebarSubscription = this.sidebarStateService.isCollapsed$.subscribe(
      (isCollapsed) => (this.isCollapsed = isCollapsed)
    );

    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(150))
      .subscribe(() => {
        this.checkScreenSize();
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.sidebarSubscription?.unsubscribe();
    this.resizeSubscription?.unsubscribe();
  }

  private checkScreenSize(): void {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;
    if (this.isMobile !== wasMobile) {
      this.sidebarStateService.collapse(this.isMobile);
    }
  }

  closeSidebarOnMobile(): void {
    if (this.isMobile) this.sidebarStateService.collapse(true);
  }

  toggleSidebar(): void {
    this.sidebarStateService.toggleSidebar();
  }

  onMenuItemClick(id: number): void {
    const routeInfo = this.routeMap[id];
    if (routeInfo) {
      this.router.navigate([routeInfo.path]);
    } else {
      console.warn(`No route mapped for ID ${id}`);
    }
  }

  handleLogout(): void {
    this.authService.logout();
  }
}